import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';

import {
  IAddTimeToCurrentTermApiPayload,
  IAddTimeToCurrentTermApiResponse,
  IAddTimeToCurrentTermThunkPayload,
  IAddTimeToCurrentTermThunkResponse,
  IAllocationItemStore,
  ICreateAllocationItemResponse,
  ICreateAllocationItemStore,
  ICreateTermBodyPayload,
  ICreateTermPayload,
  IFetchAllocationItemsPayload,
  IGetAllocationItemResponse,
  IRemoveTermApiPayload,
  IRemoveTermThunkPayload,
  IUpdateTermApiPayload,
  IUpdateTermThunkPayload
} from './interfaces';
import { ThunkAPI } from '../../../rootReducer';
import { basicApiAsyncThunkCreator } from '../../../../services/apiAsyncThunkCreator';
import { ContentTypes, CORE_SERVICES, HttpMethods } from '../../../../services/api';
import { parseError } from '../../../../utils/utils';
import { showErrorMessage } from '../../../notifications';

export const createNewTerm: AsyncThunk<ICreateAllocationItemStore, ICreateTermPayload, ThunkAPI> =
  createAsyncThunk(
    'management-createNewTerm',
    async ({ allocatedSeconds, termStartDate, accountId }, thunk) => {
      try {
        const response = await basicApiAsyncThunkCreator<ICreateAllocationItemResponse, ICreateTermBodyPayload>({
          endpoint: `/manage/term/${accountId}`,
          microservice: CORE_SERVICES.VIDEO_TRACKING_SERVICE,
          authenticated: true,
          method: HttpMethods.Post,
          contentType: ContentTypes.ApplicationJson
        })({ allocatedSeconds, termStartDate }, thunk);

        if (!response) {
          throw new Error('Could not create a new term');
        }

        return {
          id: `${response}`
        };
      } catch(err) {
        const errorMessage = parseError(err);
        return thunk.rejectWithValue(errorMessage);
      }
    }
  );

export const getManageMinutesList: AsyncThunk<IAllocationItemStore[], IFetchAllocationItemsPayload, ThunkAPI> =
  createAsyncThunk(
    'management-get-terms-list',
    async ({ accountId }, thunk) => {
      try {
        const response = await basicApiAsyncThunkCreator<IGetAllocationItemResponse[], undefined>({
          endpoint: `/manage/terms/${accountId}`,
          microservice: CORE_SERVICES.VIDEO_TRACKING_SERVICE,
          authenticated: true,
          method: HttpMethods.Get,
          contentType: ContentTypes.ApplicationJson
        })(undefined, thunk);

        if (!Array.isArray(response)) {
          throw new Error('Could not fetch the terms list');
        }

        const result: IAllocationItemStore[] = response.map(({ _id, ...otherFields }) => {
          return {
            id: _id,
            ...otherFields
          };
        });

        return result;
      } catch(err) {
        const errorMessage = parseError(err);
        return thunk.rejectWithValue(errorMessage);
      }
    }
  );

export const updateTerm: AsyncThunk<undefined, IUpdateTermThunkPayload, ThunkAPI> =
  createAsyncThunk(
    'management-update-term',
    async ({ accountId, _id, allocatedSeconds, termStartDate }, thunk) => {
      try {
        const response = await basicApiAsyncThunkCreator<void, IUpdateTermApiPayload>({
          endpoint: `/manage/term/${accountId}`,
          microservice: CORE_SERVICES.VIDEO_TRACKING_SERVICE,
          authenticated: true,
          method: HttpMethods.Put,
          contentType: ContentTypes.ApplicationJson
        })({ allocatedSeconds, _id, termStartDate }, thunk);

        if (!response) {
          throw new Error('Could not do the update');
        }

        return;
      } catch(err) {
        thunk.dispatch(showErrorMessage('Could not update'));
        const errorMessage = parseError(err);
        return thunk.rejectWithValue(errorMessage);
      }
    }
  );

export const addTimeToCurrentTerm: AsyncThunk<IAddTimeToCurrentTermThunkResponse, IAddTimeToCurrentTermThunkPayload, ThunkAPI> =
  createAsyncThunk(
    'management-add-time-to-term',
    async ({ accountId, allocatedSeconds }, thunk) => {
      try {
        const response = await basicApiAsyncThunkCreator<IAddTimeToCurrentTermApiResponse, IAddTimeToCurrentTermApiPayload>({
          endpoint: `/manage/current-term/${accountId}/add-time`,
          microservice: CORE_SERVICES.VIDEO_TRACKING_SERVICE,
          authenticated: true,
          method: HttpMethods.Post,
          contentType: ContentTypes.ApplicationJson
        })({ allocatedSeconds }, thunk);

        if (!('_id' in response)) {
          throw new Error('Could not add time to current term');
        }

        return {
          id: response._id
        };
      } catch(err) {
        const errorMessage = parseError(err);
        return thunk.rejectWithValue(errorMessage);
      }
    }
  );

export const deleteTerm: AsyncThunk<undefined, IRemoveTermThunkPayload, ThunkAPI> =
  createAsyncThunk(
    'management-delete-term',
    async ({ id, accountId }, thunk) => {
      try {
        const response = await basicApiAsyncThunkCreator<undefined, IRemoveTermApiPayload>({
          endpoint: `/manage/term/${accountId}`,
          microservice: CORE_SERVICES.VIDEO_TRACKING_SERVICE,
          authenticated: true,
          method: HttpMethods.Delete,
          contentType: ContentTypes.ApplicationJson
        })({ _id: id }, thunk);

        if (!response) {
          throw new Error('Could not add time to current term');
        }

        thunk.dispatch(getManageMinutesList({ accountId }));

        return;
      } catch(err) {
        const errorMessage = parseError(err);
        return thunk.rejectWithValue(errorMessage);
      }
    }
  );

